import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Search, Check, Trash2, ClipboardList, Lock,
    CreditCard as CreditCardIcon, Wrench, Filter, DollarSign, UserCheck, UserX
} from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { notifications as mockNotifications } from '../../data/mockData';
import { useConfirm } from '../../components/ConfirmDialog';

import { userApi } from '../../api/userApi';
import { eopApi } from '../../api/eopApi';

import { useAuth } from '../../auth/AuthContext';

import { isApiConfigured } from '../../api/axiosClient';

//import { NotificationContext } from "../contexts/NotificationContext";
import { NotificationContext } from "../../contexts/NotificationContext";
import { CsvFileDownload } from "../../components/CsvFileDownload";
import { PdfFileDownload } from "../../components/PdfFileDownload";

import { useToast } from '../../components/Toast';

import { usePermission } from '../../hooks/usePermission';
/**
 * NotificationsPage — full list of all notifications for the logged-in user.
 * Linked from the notification bell dropdown ("View all") in the top bar.
 *
 * Features:
 *   - Filter by type (All / Claims / Authorizations / Invoices / System)
 *   - Filter by read status (All / Unread / Read)
 *   - Free-text search across title + message
 *   - Mark single notification as read (click)
 *   - Mark all as read / Delete single / Delete all
 *   - Stat cards: Total, Unread, By type
 */

const TYPE_META = {
  claim:   { icon: ClipboardList,   color: '#EF4444', label: 'Claim' },
  EOP:     { icon: DollarSign, color: '#F59E0B', label: 'EOP' },
  auth:    { icon: Lock,            color: '#F59E0B', label: 'Authorization' },
  invoice: { icon: CreditCardIcon,  color: '#0D9488', label: 'Invoice' },
  system:  { icon: Wrench,          color: '#6366F1', label: 'System' },
  ProviderAdminApproval: { icon: UserCheck, color: '#16A34A', label: 'Approval' },
};

const NotificationsPage = () => {
    const { allNotificationData, refreshNotifications } =
        useContext(NotificationContext);
  const navigate = useNavigate();
    const confirm = useConfirm();
    const toast = useToast();
  //const [items, setItems] = useState(mockNotifications);
  const [items, setItems] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    //const [allNotificationData, setAllNotificationData] = useState([]);
    const [finalFilteredNotificationData, setFinalFilteredNotificationData] = useState([]);

    const [downloadingEop, setDownloadingEop] = useState(null);

    const { user } = useAuth();
    const { can, canAny, canAll } = usePermission();
    const [accessPermissions, setAccessPermissions] = useState({});


    useEffect(() => {
        setAccessPermissions({

            canApproveProviderAdmin: can(
                "superAdmin",
                "systemUsers",
                "approveProviderAdmin",
            ),
            canRejectProviderAdmin: can(
                "superAdmin",
                "systemUsers",
                "rejectProviderAdmin",
            )

        });
    }, [user]);

  /* ── Filter pipeline ─────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((n) => {
      if (typeFilter !== 'all' && n.type !== typeFilter) return false;
      if (statusFilter === 'unread' && n.read) return false;
      if (statusFilter === 'read' && !n.read) return false;
      if (
        q &&
        !n.title.toLowerCase().includes(q) &&
        !n.message.toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [items, typeFilter, statusFilter, search]);

  /* ── Stats ────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    const total = items.length;
    const unread = items.filter((n) => !n.read).length;
    const byType = Object.keys(TYPE_META).reduce((acc, k) => {
      acc[k] = items.filter((n) => n.type === k).length;
      return acc;
    }, {});
      
    return { total, unread, byType };
  }, [items]);

  /* ── Mutations ────────────────────────────────────────────── */
    const markAsRead = async(id) => {
        await markNotificationAsRead([id]);
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    }
    
  //const markAllAsRead = () =>
    //  setItems((prev) => prev.map((n) => ({ ...n, read: true })));

    const markAllAsRead = async () => {
        const ids = items
            .filter(n => !n.read)
            .map(n => n.id);

        await markNotificationAsRead(ids);

        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    const deleteOne = async(id) => {
        await deleteNotification([id]);
        setItems((prev) => prev.filter((n) => n.id !== id));    
    };
    
  const clearAll = async () => {
    const ok = await confirm({
      title: 'Clear all notifications?',
      message: `All ${items.length} notification${items.length !== 1 ? 's' : ''} will be removed from your inbox.`,
      confirmLabel: 'Clear all',
      variant: 'danger',
    });
      if (ok) {
          const ids = items              
              .map(n => n.id);

          await deleteNotification(ids);

          setItems([]);
      }
  };

  /* ── Navigation from notification click ───────────────────── */
  const handleOpen = async(n) => {
      markAsRead(n.id);
      await markNotificationAsRead([n.id]);
    // Route by type — best-effort deep link
    if (n.type === 'claim') navigate('/claims');
    else if (n.type === 'auth') navigate('/authorizations/inquiry');
    else if (n.type === 'invoice') navigate('/invoices');
    // system: stay on notifications page
  };

  /* ── Time formatting ─────────────────────────────────────── */
  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', {    
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const fullDate = (iso) =>
    new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

    

    //useEffect(async () => {
    //    await refreshNotifications();
    //}, []);

    useEffect(() => {
        const loadNotifications = async () => {
            await refreshNotifications();
        };

        loadNotifications();
    }, []);

    useEffect(() => {


        if (allNotificationData.length > 0)
        {
            const uniqueNotifications = Array.from(
                new Map(
                    allNotificationData.map(item => [
                        `${item.userId}|${item.title}|${item.message}|${item.type}|${item.referenceId}|${item.fileUrl}`,
                        item
                    ])
                ).values()
            );

            //const notifications = uniqueNotifications
            //    .filter(item => !item.fileUrl?.toLowerCase().includes("error")
            //       // && !item.isRead
            //    )
            //    .map(item => ({
            //        id: item.id,
            //        title: item?.type?.trim().toLowerCase() === 'claimeop' ? `EOP for Claim : ${item.referenceId}` : `EOP for Check : ${item.referenceId}`,
            //        checkNo: item.message?.split("|")[1]?.trim().split(/\s+/)[1] || "",
            //        message: `is ready for `,
            //        downloadUrl: item.fileUrl,
            //        type: 'EOP',
            //        read: item.isRead,
            //        date: item.createdAt,
            //      //  date: new Date(item.createdAt).toLocaleString()
            //    }));


            const getFieldValue = (text, fieldName) =>
                text.match(new RegExp(`${fieldName}\\s*:\\s*(.+)`, "i"))?.[1]?.trim() ?? "";

            const notifications = uniqueNotifications
                .filter(item =>
                    !item.fileUrl?.toLowerCase().includes("error")
                    //&& !item.isRead
                )
                .map(item => {
                    const isProviderAdminApproval =
                        item?.type?.trim().toLowerCase() === "provideradminapproval";

                    return {
                        id: item.id,
                        title: isProviderAdminApproval
                            ? item.title
                            : item?.type?.trim().toLowerCase() === "claimeop"
                                ? `EOP for Claim : ${item.referenceId}`
                                : `EOP for Check : ${item.referenceId}`,
                        checkNo: isProviderAdminApproval
                            ? ""
                            : item.message?.split("|")[1]?.trim().split(/\s+/)[1] || "",
                        others: isProviderAdminApproval
                            ? {
                                providerName: getFieldValue(item.message, "Provider\\s+Name"),
                                orgName: getFieldValue(item.message, "Organization"),
                                taxId: getFieldValue(item.message, "TaxId"),
                                registrationDate: getFieldValue(item.message, "Registration\\s+Date")
                            }
                            : null,
                        message: isProviderAdminApproval
                            ? (
                                <>
                                    {item.message.split("\n").map((line, index) => {
                                        const parts = line.split(":");

                                        return (
                                            <div key={index}>
                                                {parts.length > 1 ? (
                                                    <>
                                                        <strong>{parts[0]}</strong>
                                                        {" : "}
                                                        {parts.slice(1).join(":").trim()}
                                                    </>
                                                ) : (
                                                    line
                                                )}
                                            </div>
                                        );
                                    })}
                                </>
                            )
                            : "is ready for ",
                        downloadUrl: isProviderAdminApproval
                            ? ""
                            : item.fileUrl,
                        type: isProviderAdminApproval
                            ? "ProviderAdminApproval"
                            : "EOP",
                        read: item.isRead,
                        date: item.createdAt,
                        createdBy: item.createdBy,
                        isApproved: isProviderAdminApproval
                            ? (item.isApproved?.trim().toLowerCase() || "")
                            : "",
                        approvedBy: isProviderAdminApproval
                            ? item.approvedBy
                            : "",
                        rejectedBy: isProviderAdminApproval
                            ? item.rejectedBy
                            : "",
                    };
                });

            setItems(notifications);

            
        }
    }, [allNotificationData]);

    const handleFileDownload = async (fileName,checkNo, id) => {

       // alert("FILE NAME : " + fileName + "|" + "id : " + id + "checkNo : " + checkNo);
        //return;

        const extension = fileName.split('.').pop();
        const match = fileName.match(/_(\d+)_(\d+)\.[a-zA-Z0-9]+$/);

        let billProvider;
        let chkNumber;
        let claimNumber;
        let response;

        setDownloadingEop(id);

        if (fileName.includes("EOPClaimReport")) {

            if (match) {
                billProvider = match[1];
                claimNumber = match[2];

                response = await eopApi.downloadClaimEOP({
                    billingProvider: billProvider,
                    checkNumber: checkNo,
                    claimNumber: claimNumber,
                    fileName: fileName
                });

            }
            else {
                toast.error('Oops! Something went wrong ', { title: 'Notification' });

            }
        }
        else if (fileName.includes("EOPDetailReport")) {
            if (match) {
                billProvider = match[1];
                chkNumber = match[2];

                response = await eopApi.downloadEOP({
                    billingProvider: billProvider,
                    checkNumber: chkNumber,
                    fileName: fileName
                });

            }
            else {
                toast.error('Oops! Something went wrong ', { title: 'Notification' });
            }
        }
        else {
            toast.error('Oops! Something went wrong ', { title: 'Notification' });
        }

        if (extension?.trim().toLowerCase() === "pdf") {
            //await downloadPdfFileCard(response, fileName);
            await PdfFileDownload(response, fileName);
        }

        if (extension?.trim().toLowerCase() === "csv") {
            //await downloadCsvFileCard(response, fileName);


            const headers = [
                "Claim_Number",
                "Claim_Seq",
                "Account_No",
                "Procedure_Code",
                "Services_Provided",
                "Member_Id",
                "Member_Name",
                "Modifier",
                "Service_Date",
                "Service_Thru",
                "Svc_Count",
                "Billed_Price",
                "Plan_Price",
                "Discount_Amt",
                "Paid_Amt",
                "Copay_Amt",
                "Not_Covered_Amt",
                "Deductible_Amt",
                "Co_Ins_Amt",
                "COB_Amt",
                "Ex_Code",
                "Ex_Description",
                "Your_Rep",
                "Services_Desc",
                "TableName"
            ];
            await CsvFileDownload(response, fileName, headers);
        }

        setDownloadingEop(null);


    };

    //const downloadCsvFileCard = (response, fileName) => {


    //    const blob = new Blob([response.data], {
    //        type: "text/csv;charset=utf-8;"
    //    });

    //    const url = URL.createObjectURL(blob);

    //    const a = document.createElement("a");
    //    a.href = url;
    //    a.download = fileName;
    //    document.body.appendChild(a);
    //    a.click();

    //    document.body.removeChild(a);
    //    URL.revokeObjectURL(url);
    //};

    //const downloadPdfFileCard = (response, fileName) => {


    //    const blob = response.data;

    //    const downloadUrl = window.URL.createObjectURL(blob);

    //    const a = document.createElement("a");
    //    a.href = downloadUrl;
    //    a.download = fileName;
    //    document.body.appendChild(a);
    //    a.click();

    //    a.remove();
    //    window.URL.revokeObjectURL(downloadUrl);
    //};


    const markNotificationAsRead = async (notfIds) => {
        //alert(notfIds);

        if (isApiConfigured) {

            try {
                const response = await userApi.markUserNotificationAsRead({
                    ids: notfIds, //array
                    userId: user?.userId
                });
                await refreshNotifications();

            } catch (err) {

                console.log("Error to mark notification as read");

            } finally {
            }
        }
    };

    const deleteNotification = async (notfIds) => {
        //alert(notfIds);

        if (isApiConfigured) {

            try {
                const response = await userApi.deleteUserNotification({
                    ids: notfIds, //array
                    userId: user?.userId
                });

                await refreshNotifications();


            } catch (err) {

                console.log("Error to delete notification");

            } finally {
            }
        }
    };

    const approveOrRejectUser = async (userId, apprStatus, others) => {



        const nextStatus = apprStatus;
        const next = apprStatus;

        const providerName = others.providerName;
        const orgName = others.orgName;
        const role = "Provider Admin";

        const ok = await confirm({
            title: `${next ? "Approve" : "Reject"} ${providerName}?`,
            message: `${orgName} · ${role}. ${next === true
                ? `${providerName} will gain access to the portal.`
                : `${providerName} will be rejected.`
                }`,
            confirmLabel: next === true ? 'Approve' : 'Reject',
            variant: next === true ? 'approve' : 'reject',
        });

        
        if (!ok) return;
        const displayName = `${providerName}`;

        let rejectReason = "";
        if (!nextStatus) {
            rejectReason = ok.reason
        }

        if (isApiConfigured()) {

            userApi.approveUser({
                userId: userId,
                currentapproveStatus: nextStatus,
                rejectReason: rejectReason
            })
                .then(async (res) => {

                    if (res.success) {
                        toast.success(
                            `${displayName} is now ${next ? "Approved" : "Rejected"}`,
                            {
                                title: next ? "User approved" : "User rejected"
                            }
                        );
                    }
                    else {
                        toast.warning(
                            res.message,
                            {
                                title: "User Approved"
                            }
                        );

                    }

                    await refreshNotifications();


                })
                .catch((err) => {

                    console.error(err);

                    toast.error(err.message || 'Failed to Process', {
                        title: 'Error',
                    });
                });
            //.catch((err) => toast.error(err.message || 'Failed to toggle status', { title: 'Status change failed' }));
        } else {
            setUsers(users.map(x => x.id === userId ? { ...x, isApproved: next } : x));
            toast.success(
                `${displayName} is now ${next}.`,
                { title: next === 'Active' ? 'User Approved' : 'User Disapproved' }
            );
        }
    };

    
  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Bell size={22} color="#0D9488" strokeWidth={2} />
            <h1 style={{ margin: 0 }}>Notifications</h1>
          </div>
          <p>All notifications for your account</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {stats.unread > 0 && (
            <button className="btn-secondary" onClick={markAllAsRead}>
              <Check size={15} strokeWidth={2.5} style={{ marginRight: 6 }} />
              Mark all as read
            </button>
          )}
          {stats.total > 0 && (
            <button
              onClick={clearAll}
              style={{
                padding: '10px 18px', borderRadius: 12,
                border: '1.5px solid rgba(239,68,68,0.3)',
                background: 'rgba(239,68,68,0.06)', color: '#DC2626',
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Trash2 size={15} strokeWidth={2.5} />
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
          <div className="stat-cards"
              /*style={{ gridTemplateColumns: 'repeat(6, 1fr)', marginBottom: 20 }}*/
              style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: 20,
              }}
          >
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon teal"><Bell size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
                  <div className="stat-icon pink"><Bell size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.unread}</div>
          <div className="stat-label">Unread</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon danger"><ClipboardList size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.byType.claim}</div>
          <div className="stat-label">Claims</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
                    <div className="stat-icon orange"><DollarSign size={20} strokeWidth={2} /></div>
                    <div className="stat-value">{stats.byType.EOP}</div>
                    <div className="stat-label">EOPs</div>
              </div>

              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
                  <div className="stat-icon green"><UserCheck size={20} strokeWidth={2} /></div>
                  <div className="stat-value">{stats.byType.ProviderAdminApproval}</div>
                  <div className="stat-label">Approval</div>
              </div>
        {/*      <div className="stat-card" style={{*/}
        {/*          display: 'flex',*/}
        {/*          flexDirection: 'column',*/}
        {/*          alignItems: 'center',*/}
        {/*          justifyContent: 'center',*/}
        {/*          textAlign: 'center',*/}
        {/*      }}>*/}
        {/*  <div className="stat-icon orange"><Lock size={20} strokeWidth={2} /></div>*/}
        {/*  <div className="stat-value">{stats.byType.auth}</div>*/}
        {/*  <div className="stat-label">Authorizations</div>*/}
        {/*</div>*/}
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon teal"><CreditCardIcon size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.byType.invoice}</div>
          <div className="stat-label">Invoices</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon blue"><Wrench size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.byType.system}</div>
          <div className="stat-label">System</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
              <div
                  style={{
                          display: 'grid',
                       //   gridTemplateColumns: '2fr 1fr 1fr',
                          gap: 14,
              }}>
                  <div className="row">
                      <div className="col-sm-6">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                              <label>Search</label>
                              <div style={{ position: 'relative' }}>
                                  <span style={{
                                      position: 'absolute', left: 12, top: '50%',
                                      transform: 'translateY(-50%)', color: '#9CA3AF', display: 'flex',
                                  }}>
                                      <Search size={16} />
                                  </span>
                                  <input
                                      type="search"
                                      placeholder="Search title or message..."
                                      value={search}
                                      onChange={(e) => setSearch(e.target.value)}
                                      style={{ paddingLeft: 38 }}
                                  />
                              </div>
                          </div>
                      </div>
                      <div className="col-sm-3">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                              <label>Type</label>
                              <select name="selectNotType" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                  <option value="all">All Types</option>
                                  <option value="claim">Claims</option>
                                  {/*<option value="auth">Authorizations</option>*/}
                                  <option value="invoice">Invoices</option>
                                  <option value="system">System</option>
                                  <option value="EOP">EOP</option>
                                  <option value="ProviderAdminApproval">Approval</option>
                              </select>
                          </div>
                      </div>
                      <div className="col-sm-3">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                              <label>Status</label>
                              <select name="selectNotStatus" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                  <option value="all">All</option>
                                  <option value="unread">Unread only</option>
                                  <option value="read">Read only</option>
                              </select>
                          </div>
                      </div>
                  </div>
          
          
          
        </div>
      </div>

      {/* Notification list */}
      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 56 }}>
          <Bell size={44} color="#CBD5E1" strokeWidth={1.5} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 17, fontWeight: 600, color: '#1A1D21', marginBottom: 4 }}>
            {items.length === 0 ? 'No notifications' : 'No notifications match your filters'}
          </div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            {items.length === 0
              ? "You're all caught up."
              : 'Try clearing the filters or adjusting the search.'}
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {filtered.map((n, idx) => {
            const meta = TYPE_META[n.type] || { icon: Bell, color: '#6B7280', label: 'Other' };
            const Icon = meta.icon;
            return (
              <div
                key={n.id}
                style={{
                  //display: 'flex',
                  //gap: 16,
                  padding: '18px 22px',
                  background: n.read ? '#fff' : 'rgba(13,148,136,0.04)',
                  borderBottom: idx < filtered.length - 1 ? '1px solid #F1F5F9' : 'none',
                  transition: 'background 0.15s',
                  cursor: 'pointer',
                }}
                onClick={() => handleOpen(n)}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFB'; }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = n.read ? '#fff' : 'rgba(13,148,136,0.04)';
                }}
                >
                    <div className="row">
                        <div className="col-sm-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* Type icon */}
                            <div style={{
                                width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                                background: `${meta.color}14`,
                                display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Icon size={19} color={meta.color} strokeWidth={2} />
                            </div>
                        </div>
                        <div className="col-sm-8">
                            {/* Content */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'flex-start', gap: 12, marginBottom: 4,
                                }}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1,
                                    }}>
                                        {!n.read && (
                                            <span style={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                background: '#0D9488', flexShrink: 0,
                                            }} />
                                        )}
                                        <span style={{
                                            fontSize: 11, fontWeight: 700,
                                            color: meta.color, textTransform: 'uppercase',
                                            letterSpacing: '0.05em', flexShrink: 0,
                                        }}>
                                            {meta.label}
                                        </span>
                                        <span style={{
                                            fontSize: 14, fontWeight: n.read ? 500 : 700,
                                            color: '#1A1D21', overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {n.title}
                                        </span>
                                    </div>
                                    <div
                                        style={{ fontSize: 12, color: '#9CA3AF', flexShrink: 0 }}
                                        title={fullDate(n.date)}
                                    >
                                        {timeAgo(n.date)}
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: 13, color: '#6B7280', lineHeight: 1.5,
                                }}>
                                    
                                    {n.message}
                                    {/*<div style={{ whiteSpace: "pre-line" }}>*/}
                                    {/*    {n.message}*/}
                                    {/*</div>*/}
                                    {n.type === "ProviderAdminApproval"
                                        ?
                                        n.isApproved === "pending" ? (
                                            <>
                                                {accessPermissions.canApproveProviderAdmin && (
                                                    <>
                                                        <button
                                                            name="btnApprove"
                                                            onClick={async () => await approveOrRejectUser(n.createdBy, true, n.others)}
                                                            title={`Approve ${n.others.providerName}`}
                                                            style={{
                                                                padding: '5px 10px',
                                                                borderRadius: 8,
                                                                border: 'none',
                                                                background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                                                                color: '#fff',
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                marginTop: '10px',
                                                                gap: 4,
                                                                fontFamily: 'inherit',
                                                                boxShadow: '0 2px 6px rgba(139,92,246,0.3)',
                                                            }}
                                                        >
                                                            <UserCheck size={12} strokeWidth={2.5} />
                                                            Approve
                                                        </button>
                                                    </>
                                                )}
                                                
                                                {accessPermissions.canRejectProviderAdmin && (
                                                    <>
                                                        <button
                                                            name="btnReject"
                                                            onClick={async () => await approveOrRejectUser(n.createdBy, false, n.others)}
                                                            title={`Reject ${n.others.providerName}`}
                                                            style={{
                                                                padding: '5px 10px',
                                                                borderRadius: 8,
                                                                border: 'none',
                                                                marginLeft: '5px',
                                                                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                                                                color: '#fff',
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                fontFamily: 'inherit',
                                                                boxShadow: '0 2px 6px rgba(139,92,246,0.3)',
                                                            }}
                                                        >
                                                            <UserX size={12} strokeWidth={2.5} />
                                                            Reject
                                                        </button>
                                                    </>
                                                )}
                                                
                                            </>
                                        ) : (
                                            <>
                                                {(n.approvedBy?.trim() || n.rejectedBy?.trim()) && (
                                                    <>
                                                        <br />
                                                        <strong>
                                                            {n.isApproved === "true" ? "Approved By:" : "Rejected By:"}
                                                        </strong>{" "}
                                                        {n.isApproved === "true" ? n.approvedBy : n.rejectedBy}
                                                    </>
                                                )}
                                            </>
                                        )
                                        :
                                        <>
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleFileDownload(n.downloadUrl, n.checkNo, n.id);
                                                }}
                                            >
                                                {downloadingEop === n.id
                                                    ? "Downloading..."
                                                    : "Download"
                                                }


                                                <br></br>
                                            </a>
                                            {` File : ${n.downloadUrl}`}
                                        </>
                                    }
                                    
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'flex-start' }}>
                                {!n.read && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                                        title="Mark as read"
                                        style={iconButtonStyle('#0D9488')}
                                    >
                                        <Check size={15} strokeWidth={2.5} />
                                    </button>
                                )}
                                <button
                                    onClick={(e) => { e.stopPropagation(); deleteOne(n.id); }}
                                    title="Delete"
                                    style={iconButtonStyle('#DC2626')}
                                >
                                    <Trash2 size={15} strokeWidth={2.5} />
                                </button>
                            </div>
                        </div>
                    </div>
                
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{
          marginTop: 12, fontSize: 12, color: '#9CA3AF',
          textAlign: 'center',
        }}>
          Showing {filtered.length} of {items.length} notification{items.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

const iconButtonStyle = (color) => ({
  width: 30, height: 30, borderRadius: 8,
  border: `1px solid ${color}33`,
  background: `${color}0A`,
  color,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s',
});

export default NotificationsPage;
